// frontend/packages/business-console/src/components/build-interview/ReviewConfirm.jsx
//
// Review & confirm screen (Website Builder Redesign, Phase 1): the primary
// edit/go-back surface. One row per brief section (status dot, name, summary,
// Edit or + Add), a Start over control, and a confirm bar whose copy reassures
// that confirming is not a lock-in. Edit / Add jump back to the step (reuses
// editingStepId); Start over calls the reset endpoint via the page.
//
// Colors via useTokens; copy is em-dash-free.

import React from 'react';
import { Box, Button, Typography, Alert, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import useTokens from '../primitives/useTokens';
import { SECTION_LABELS, summarizeSection } from './briefSummary';

function Row({ step, value, busy, onEdit, tokens }) {
  const t = tokens;
  const summary = value === undefined ? 'Skipped' : summarizeSection(step.id, value);
  const answered = summary && summary !== 'Skipped';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '28px 140px 1fr auto',
        alignItems: 'center',
        gap: 1,
        py: 1,
        borderTop: `1px solid ${t.hairline}`,
      }}
    >
      {answered ? (
        <CheckCircleIcon sx={{ fontSize: 18, color: t.green }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: t.faint }} />
      )}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: answered ? t.text : t.faint,
        }}
      >
        {step.title}
      </Typography>
      <Typography
        sx={{
          fontSize: 12.5,
          color: answered ? t.textSecondary : t.faint,
          fontStyle: answered ? 'normal' : 'italic',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {answered ? summary : "Skipped, that's fine"}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        onClick={() => onEdit && onEdit(step.id)}
        startIcon={answered ? <EditOutlinedIcon /> : <AddIcon />}
        sx={{
          borderColor: answered ? t.blueBorder : t.grayBorder,
          color: answered ? t.blue : t.muted,
          fontSize: 11.5,
          fontWeight: 700,
          px: 1.25,
          minWidth: 0,
        }}
      >
        {answered ? 'Edit' : 'Add'}
      </Button>
    </Box>
  );
}

export default function ReviewConfirm({
  steps = [],
  data = null,
  busy = false,
  readyToConfirm = false,
  teamForkIntro = '',
  onEdit,
  onConfirm,
  onStartOver,
}) {
  const t = useTokens();
  const sectionSteps = steps.filter((s) => SECTION_LABELS[s.id]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: t.text }}>
            Review and confirm your brief
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: t.muted, mt: 0.25 }}>
            Tap Edit on any line to jump back to that step. Your answers stay
            saved.
          </Typography>
        </Box>
        <Button
          size="small"
          disabled={busy}
          onClick={onStartOver}
          startIcon={<RestartAltIcon />}
          sx={{ color: t.muted, flexShrink: 0 }}
        >
          Start over
        </Button>
      </Box>

      {teamForkIntro ? (
        <Alert severity="info" icon={<GroupsIcon />} sx={{ my: 1.5 }}>
          {teamForkIntro}
        </Alert>
      ) : null}

      <Box sx={{ mt: 1.5 }}>
        {sectionSteps.map((s) => (
          <Row
            key={s.id}
            step={s}
            value={data ? data[s.id] : undefined}
            busy={busy}
            onEdit={onEdit}
            tokens={t}
          />
        ))}
      </Box>

      <Box
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: '12px',
          background: t.greenBg,
          border: `1px solid ${t.greenBorder}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 22, color: t.green, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: t.greenText, lineHeight: 1.5 }}>
          Confirming starts the build, but you are not locked in. You can edit any
          answer and rebuild, or tweak the finished site directly, anytime.
        </Typography>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        <Button
          variant="contained"
          size="large"
          disabled={busy || !readyToConfirm}
          onClick={onConfirm}
          sx={{
            background: t.gradient,
            borderRadius: '10px',
            fontWeight: 800,
            px: 3,
            boxShadow: '0 6px 16px rgba(37,99,235,.3)',
            '&:hover': { background: t.gradient, filter: 'brightness(1.05)' },
          }}
        >
          Confirm and build my site
        </Button>
      </Stack>
    </Box>
  );
}
